// A service line's split defaults (TD-078): the agency's target share, how the rest of a price splits between work
// types, and the catch-up rate on a price increase. A contract line's split prefills from them only once the owner turns
// the split on. The service line's modal edits them in whole percents of a price.
import type { ServiceLine, WorkValues } from "../types";
import { lrSplit, workTotal } from "./workValues";

export interface SplitDefaults {
  /** The agency's target share, a whole percent. */
  targetPct: number;
  /** Work type id -> weight. */
  proportions: WorkValues;
  catchUpPct: number;
}

/** The catch-up rate a service line with no rate set uses (backend pricing_types.DEFAULT_CATCH_UP_PCT). */
export const DEFAULT_CATCH_UP_PCT = 80;

/**
 * A service line's defaults, as the server reads them (contract_service.split_defaults): a target and some work weighted,
 * else none. A null catch-up rate reads DEFAULT_CATCH_UP_PCT.
 */
export function splitDefaults(line: ServiceLine | undefined): SplitDefaults | null {
  if (!line || line.agencyTargetPct === null || line.workProportions === null) return null;
  if (workTotal(line.workProportions) <= 0) return null;
  return { targetPct: line.agencyTargetPct, proportions: line.workProportions, catchUpPct: line.catchUpPct ?? DEFAULT_CATCH_UP_PCT };
}

/** The modal's form: each work type's whole percent of a price (the agency keeps the rest), and the catch-up rate as typed. */
export interface DefaultsForm {
  pcts: WorkValues;
  catchUp: string;
}

/** The stored defaults as whole percents: what the target leaves, split by the weights, the spare percents by largest remainder. */
export function defaultsForm(line: ServiceLine): DefaultsForm {
  const defaults = splitDefaults(line);
  return { pcts: defaults ? lrSplit(100 - defaults.targetPct, defaults.proportions) : {}, catchUp: defaults ? String(defaults.catchUpPct) : "" };
}

/** Some work weighted: the form holds defaults, so it asks for the catch-up rate too. */
export const hasSplit = (pcts: WorkValues): boolean => workTotal(pcts) > 0;

export interface DefaultsCheck {
  splitError: string | undefined;
  catchUpError: string | undefined;
  blocked: boolean;
}

export function defaultsCheck({ pcts, catchUp }: DefaultsForm): DefaultsCheck {
  const splitError = workTotal(pcts) > 100 ? "The work types add up to more than 100%" : undefined;
  const rate = Number(catchUp);
  const badRate = catchUp.trim() === "" || !Number.isInteger(rate) || rate < 0 || rate > 100;
  const catchUpError = hasSplit(pcts) && badRate ? "A whole number from 0 to 100" : undefined;
  return { splitError, catchUpError, blocked: !!splitError || !!catchUpError };
}

/** What a save sends: the three defaults, the agency keeping what the work leaves, or three nulls when no work is weighted. */
export function defaultsPatch({ pcts, catchUp }: DefaultsForm): { agencyTargetPct: number | null; workProportions: WorkValues | null; catchUpPct: number | null } {
  if (!hasSplit(pcts)) return { agencyTargetPct: null, workProportions: null, catchUpPct: null };
  return { agencyTargetPct: 100 - workTotal(pcts), workProportions: pcts, catchUpPct: Number(catchUp) };
}
